import React from "react";
import PropTypes from "prop-types";
import StoreFilter from "./StoreFilter";
import CategoryFilterOption from "./CategoryFilterOption";
import mobileStyle from "../../stylesheets/MobileFilter.module.scss";
import style from "../../stylesheets/StoreFilter.module.scss";
import { CATEGORY_FACET_ID, CLOSE_MENU_PSEUDO_FACET_ID } from "../../constants";

function CategoryFilter(props) {
  const {
    facetFilter,
    selectedDropdown,
    showDropdown,
    showingLowestCategoryLevel,
    correspondingFacetParamTagIds,
    categoryListOfFilters,
    categoryTargetFilter,
    parentFilterExists,
    handleCategoryFilterClick,
    slideCategoryFilters,
    loadFiltersAndResults,
    fakeParentStoreExists,
    isMobile,
  } = props;

  if (facetFilter.active_facet_tags.length === 0) {
    return null;
  }

  const previousFilterParentId = categoryTargetFilter.grandparent_id;
  const previousFilterParentName = categoryTargetFilter.grandparent_name;
  const isFakeStore =
    Object.keys(categoryTargetFilter).length === 0 &&
    correspondingFacetParamTagIds.length === 0;
  const fakeAncestorStoreExists =
    showingLowestCategoryLevel &&
    parentFilterExists &&
    !previousFilterParentId &&
    !previousFilterParentName;
  const ancestorExists =
    fakeAncestorStoreExists ||
    (previousFilterParentName && previousFilterParentId);

  let backToFilterId = categoryTargetFilter.parent_id;
  let backToFilterName = categoryTargetFilter.parent_name;

  if (previousFilterParentId && previousFilterParentName) {
    backToFilterId = previousFilterParentId;
    backToFilterName = previousFilterParentName;
  } else if (fakeParentStoreExists || fakeAncestorStoreExists) {
    backToFilterId = null;
    backToFilterName = "All";
  }

  const handleClickParent = (event) => {
    event.preventDefault();
    delete categoryTargetFilter.ancestors[categoryTargetFilter.id];

    const clickedParentFilter = {
      facet_id: CATEGORY_FACET_ID,
      id: backToFilterId,
      ancestors: categoryTargetFilter.ancestors,
      available_facets: categoryTargetFilter.available_facets,
    };
    handleCategoryFilterClick(clickedParentFilter);
  };

  const previousFilter =
    (!showingLowestCategoryLevel && parentFilterExists) ||
    (showingLowestCategoryLevel && ancestorExists) ||
    fakeParentStoreExists ? (
      <li
        className={`${style.backToParent} ${mobileStyle.backToParent}`}
        onClick={handleClickParent}
        value={backToFilterId}
      >
        <img
          className={style.filterBack}
          src="https://cdn-s3.touchofmodern.com/images/icons/icon_filterBackArrow%402x.png"
          alt="filterBack"
        />
        <div className={style.backToTitle}>Back to {backToFilterName}</div>
      </li>
    ) : null;

  let isCurrentFilterShowing = false;
  let isCurrentFilterSameAsShowAllFilter = isFakeStore;

  const targetShowAllFilter = showingLowestCategoryLevel
    ? categoryTargetFilter.parent_id
    : categoryTargetFilter.id;

  const filterOptions = categoryListOfFilters.map((filterOption) => {
    let isCurrentFilter = false;

    correspondingFacetParamTagIds.forEach((filterId) => {
      if (Number(filterId) === filterOption.id) {
        isCurrentFilter = true;
      }

      if (Number(filterId) === targetShowAllFilter) {
        isCurrentFilterSameAsShowAllFilter = true;
      }
    });

    if (filterOption.id === categoryTargetFilter.id) {
      isCurrentFilter = true;
      isCurrentFilterShowing = true;
    }

    filterOption.count = filterOption.count || 0;
    return (
      <CategoryFilterOption
        key={filterOption.id}
        showingLowestCategoryLevel={showingLowestCategoryLevel}
        facetName={facetFilter.facet.display_name}
        filterOption={filterOption}
        isCurrentFilter={isCurrentFilter}
        handleCategoryFilterClick={handleCategoryFilterClick}
        showDropdown={showDropdown}
        isMobile={isMobile}
      />
    );
  });

  let showAllFilterId = categoryTargetFilter.id;
  let showAllFilterName = categoryTargetFilter.name;
  let showAllFilterStyle = isCurrentFilterSameAsShowAllFilter
    ? style.showAllFilter
    : "";

  if (Object.keys(categoryTargetFilter).length === 0) {
    showAllFilterId = null;
    showAllFilterName = "";
  } else if (isCurrentFilterShowing) {
    showAllFilterId = categoryTargetFilter.parent_id;
    showAllFilterName = categoryTargetFilter.parent_name;
    showAllFilterStyle = "";
  }

  const showAllCategories = (event) => {
    event.preventDefault();
    if (!isMobile) {
      if (isCurrentFilterSameAsShowAllFilter) {
        showDropdown(CLOSE_MENU_PSEUDO_FACET_ID);
        return;
      }

      if (showAllFilterId === null) {
        // go to the fake store
        loadFiltersAndResults({ params: {} });
        return;
      }
    }

    const clickedFilter = {
      facet_id: CATEGORY_FACET_ID,
      facet_name: "Category",
      id: showAllFilterId,
      name: showAllFilterName,
      ancestors: categoryTargetFilter.ancestors || {},
      available_facets: categoryTargetFilter.available_facets || [],
    };

    handleCategoryFilterClick(clickedFilter);
  };

  const showAllFilter = (
    <li
      className={showAllFilterStyle}
      onClick={showAllCategories}
      value={showAllFilterId}
    >
      All {showAllFilterName}
    </li>
  );

  const storeFilterProps = {
    facetFilter,
    selectedDropdown,
    showDropdown,
    isMobile,
  };

  return isMobile ? (
    <div className={mobileStyle.filterWrapper}>
      {facetFilter.facet.display_name}
      <div className={mobileStyle.categoryFilterDropdown} tabIndex="0">
        <div id="category-loading" className={style.categoryLoading} />
        <ul>
          {previousFilter}
          {showAllFilter}
          {filterOptions}
        </ul>
      </div>
    </div>
  ) : (
    <StoreFilter {...storeFilterProps}>
      <div id="category-loading" className={style.categoryLoading} />
      <ul className={style.scrollableFilterDropdown}>
        {previousFilter}
        {showAllFilter}
        {filterOptions}
      </ul>
    </StoreFilter>
  );
}

CategoryFilter.propTypes = {
  facetFilter: PropTypes.object.isRequired,
  categoryTargetFilter: PropTypes.object.isRequired,
  selectedDropdown: PropTypes.number.isRequired,
  showDropdown: PropTypes.func.isRequired,
  correspondingFacetParamTagIds: PropTypes.array.isRequired,
  categoryListOfFilters: PropTypes.array.isRequired,
  parentFilterExists: PropTypes.bool.isRequired,
  showingLowestCategoryLevel: PropTypes.bool.isRequired,
  handleCategoryFilterClick: PropTypes.func.isRequired,
  slideCategoryFilters: PropTypes.func.isRequired,
  loadFiltersAndResults: PropTypes.func.isRequired,
  fakeParentStoreExists: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default CategoryFilter;
